import type {
  Delivery,
  DeliveryItem,
  DeliveryStatus,
  ReceptionRecord,
  ReceptionStatus,
} from '../types';
import { dataService } from './dataService';

// ============================================================================
// WARNING: PERSISTENCIA CON localStorage - PARA DEMO SOLAMENTE
// ============================================================================
// Este módulo usa localStorage para persistir entregas entre recargas.
// ESTO NO ES ADECUADO PARA PRODUCCIÓN por:
//   - Limitado a 5-10MB por dominio
//   - No sincroniza entre usuarios/dispositivos
//   - No hay validación server-side
//   - No hay backup automático
//   - Datos se pierden si el usuario limpia el navegador
//
// EN PRODUCCIÓN, se necesita implementar:
//   1. POST   /api/deliveries                     - Crear nueva entrega
//   2. GET    /api/deliveries                     - Listar entregas
//   3. GET    /api/deliveries/:id                 - Detalle de entrega
//   4. PUT    /api/deliveries/:id                 - Actualizar entrega
//   5. PATCH  /api/deliveries/:id/status          - Cambiar estado
//   6. POST   /api/deliveries/:id/reception       - Confirmar recepción
//   7. DELETE /api/deliveries/:id                 - Eliminar entrega
//   8. GET    /api/deliveries/wallboard/:location - Feed wallboard
//
// Consideraciones adicionales:
//   - WebSocket o SSE para actualización en tiempo real del wallboard
//   - Integración con sistema ERP (NetSuite) para POs
//   - Notificaciones push para cambios de estado
//   - Geolocalización de embarcaciones para ETA real
//   - Histórico de recepciones para análisis
//
// ⚠️ NO USAR EN PRODUCCIÓN SIN MIGRAR A BACKEND CORPORATIVO
// ============================================================================

const DELIVERIES_KEY = 'interbarge_deliveries';
const RECEPTIONS_KEY = 'interbarge_receptions';

class DeliveryService {
  // =========================================================================
  // CRUD - Deliveries
  // =========================================================================

  getAll(): Delivery[] {
    try {
      const stored = localStorage.getItem(DELIVERIES_KEY);
      if (!stored) {
        // Inicializar con mock data
        const mockData = this.generateMockDeliveries();
        this.saveAll(mockData);
        return mockData;
      }
      return JSON.parse(stored) as Delivery[];
    } catch (error) {
      console.error('[DeliveryService] Error cargando entregas:', error);
      return [];
    }
  }

  getById(id: string): Delivery | null {
    const deliveries = this.getAll();
    return deliveries.find(d => d.id === id) || null;
  }

  getByLocation(locationName: string): Delivery[] {
    const deliveries = this.getAll();
    return deliveries.filter(d => d.locationName === locationName);
  }

  getActiveByLocation(locationName: string): Delivery[] {
    return this.getByLocation(locationName).filter(
      d => d.status !== 'cancelled'
    );
  }

  create(data: Omit<Delivery, 'id' | 'createdAt' | 'updatedAt'>): Delivery {
    const deliveries = this.getAll();
    const now = new Date().toISOString();

    const newDelivery: Delivery = {
      ...data,
      id: crypto.randomUUID(),
      createdAt: now,
      updatedAt: now,
    };

    deliveries.push(newDelivery);
    this.saveAll(deliveries);
    this.emitUpdate();
    return newDelivery;
  }

  update(id: string, data: Partial<Delivery>): Delivery | null {
    const deliveries = this.getAll();
    const index = deliveries.findIndex(d => d.id === id);
    if (index === -1) return null;

    deliveries[index] = {
      ...deliveries[index],
      ...data,
      id, // Preserve id
      updatedAt: new Date().toISOString(),
    };

    this.saveAll(deliveries);
    this.emitUpdate();
    return deliveries[index];
  }

  updateStatus(id: string, status: DeliveryStatus): Delivery | null {
    const update: Partial<Delivery> = { status };
    if (status === 'at_port' || status === 'received') {
      update.actualArrival = new Date().toISOString();
    }
    return this.update(id, update);
  }

  delete(id: string): boolean {
    const deliveries = this.getAll();
    const filtered = deliveries.filter(d => d.id !== id);
    if (filtered.length === deliveries.length) return false;

    this.saveAll(filtered);
    this.emitUpdate();
    return true;
  }

  // =========================================================================
  // CRUD - Reception Records
  // =========================================================================

  getReceptions(): ReceptionRecord[] {
    try {
      const stored = localStorage.getItem(RECEPTIONS_KEY);
      if (!stored) return [];
      return JSON.parse(stored) as ReceptionRecord[];
    } catch (error) {
      console.error('[DeliveryService] Error cargando recepciones:', error);
      return [];
    }
  }

  getReceptionsByDelivery(deliveryId: string): ReceptionRecord[] {
    return this.getReceptions().filter(r => r.deliveryId === deliveryId);
  }

  confirmReception(data: {
    deliveryId: string;
    receivedBy: string;
    status: ReceptionStatus;
    observations?: string;
    itemsReceived?: { itemId: string; receivedQuantity: number }[];
  }): ReceptionRecord {
    const receptions = this.getReceptions();
    const now = new Date().toISOString();

    const record: ReceptionRecord = {
      id: crypto.randomUUID(),
      deliveryId: data.deliveryId,
      receivedBy: data.receivedBy,
      receivedAt: now,
      status: data.status,
      observations: data.observations,
      itemsReceived: data.itemsReceived,
    };

    receptions.push(record);
    localStorage.setItem(RECEPTIONS_KEY, JSON.stringify(receptions));

    // Update delivery status
    const newStatus: DeliveryStatus = data.status === 'rechazado'
      ? 'delayed' // Rechazado vuelve a delayed para re-proceso
      : 'received';

    this.updateStatus(data.deliveryId, newStatus);

    // If items received, update delivery items
    if (data.itemsReceived && data.itemsReceived.length > 0) {
      const delivery = this.getById(data.deliveryId);
      if (delivery) {
        const updatedItems = delivery.items.map(item => {
          const received = data.itemsReceived?.find(r => r.itemId === item.id);
          if (received) {
            return { ...item, receivedQuantity: received.receivedQuantity };
          }
          return item;
        });
        this.update(data.deliveryId, { items: updatedItems });
      }
    }

    this.emitUpdate();
    return record;
  }

  // =========================================================================
  // Stats
  // =========================================================================

  getStats() {
    const deliveries = this.getAll();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const todayDeliveries = deliveries.filter(d => {
      const eta = new Date(d.estimatedArrival);
      return eta >= today && eta < tomorrow;
    });

    return {
      total: deliveries.length,
      today: todayDeliveries.length,
      inTransit: deliveries.filter(d => d.status === 'in_transit').length,
      atPort: deliveries.filter(d => d.status === 'at_port').length,
      receiving: deliveries.filter(d => d.status === 'receiving').length,
      delayed: deliveries.filter(d => d.status === 'delayed').length,
      received: deliveries.filter(d => d.status === 'received').length,
      scheduled: deliveries.filter(d => d.status === 'scheduled').length,
      cancelled: deliveries.filter(d => d.status === 'cancelled').length,
    };
  }

  getLocations(): string[] {
    try {
      return dataService.getLocations();
    } catch {
      // Fallback si dataService no tiene datos cargados
      return [
        'REM. ALIANZA I',
        'REM. ALIANZA II',
        'REM. ALIANZA III',
        'REM. ALIANZA IV',
        'REM. ALIANZA V',
        'REM. CRUZ DEL SUR',
        'REM. SIRIUS',
        'REM. VEGA',
        'REM. ANTARES',
        'REM. CANOPUS',
        'OFICINA CENTRAL',
        'TALLER NAVAL',
      ];
    }
  }

  // =========================================================================
  // Internals
  // =========================================================================

  private saveAll(deliveries: Delivery[]): void {
    localStorage.setItem(DELIVERIES_KEY, JSON.stringify(deliveries));
  }

  private emitUpdate(): void {
    window.dispatchEvent(new CustomEvent('deliveriesUpdated'));
  }

  resetMockData(): void {
    const mockData = this.generateMockDeliveries();
    this.saveAll(mockData);
    localStorage.removeItem(RECEPTIONS_KEY);
    this.emitUpdate();
  }

  // =========================================================================
  // Mock Data Generator
  // =========================================================================

  private generateMockDeliveries(): Delivery[] {
    const now = new Date();
    const locations = dataService.getLocations();

    // Select real suppliers (top ones by frequency)
    const supplierStats = dataService.getSupplierStats();
    const topSuppliers = supplierStats.slice(0, 30);

    // Select a diverse set of locations
    const selectedLocations = locations.length > 0
      ? locations.slice(0, Math.min(12, locations.length))
      : [
          'REM. ALIANZA I', 'REM. ALIANZA II', 'REM. ALIANZA III',
          'REM. CRUZ DEL SUR', 'REM. SIRIUS', 'REM. VEGA',
          'REM. ANTARES', 'REM. CANOPUS', 'OFICINA CENTRAL',
        ];

    // Categories for realistic items
    const categories = dataService.getCategories();
    const itemTemplates = [
      { desc: 'Aceite Hidráulico ISO 68', cat: 'Lubricantes', qty: 200, unit: 'litros' },
      { desc: 'Cables de Acero 3/4"', cat: 'Cabos y Cables', qty: 500, unit: 'metros' },
      { desc: 'Filtros de Combustible Marine', cat: 'Filtros', qty: 24, unit: 'unidades' },
      { desc: 'Pintura Antifouling', cat: 'Pinturas', qty: 80, unit: 'litros' },
      { desc: 'Juntas de Motor Principal', cat: 'Repuestos Motor', qty: 12, unit: 'juegos' },
      { desc: 'Raciones de Tripulación', cat: 'Provisiones', qty: 150, unit: 'kg' },
      { desc: 'Equipos de Seguridad', cat: 'Seguridad Marítima', qty: 6, unit: 'kits' },
      { desc: 'Baterías Marinas 12V', cat: 'Electricidad', qty: 8, unit: 'unidades' },
      { desc: 'Mangueras Hidráulicas', cat: 'Hidráulica', qty: 15, unit: 'metros' },
      { desc: 'Válvulas de Bola DN50', cat: 'Válvulas y Accesorios', qty: 6, unit: 'unidades' },
      { desc: 'Cadena Ancla 22mm', cat: 'Fondeo', qty: 100, unit: 'metros' },
      { desc: 'Aislamiento Térmico', cat: 'Aislamiento', qty: 40, unit: 'm²' },
      { desc: 'Bombas de Sentina', cat: 'Bombas', qty: 2, unit: 'unidades' },
      { desc: 'Kit Primeros Auxilios Marine', cat: 'Seguridad', qty: 4, unit: 'kits' },
      { desc: 'Agua Potable', cat: 'Provisiones', qty: 5000, unit: 'litros' },
      { desc: 'Combustible Marine Gas Oil', cat: 'Combustible', qty: 15000, unit: 'litros' },
      { desc: 'Oxígeno Industrial', cat: 'Gases Industriales', qty: 10, unit: 'tubos' },
      { desc: 'Electrodos de Soldadura', cat: 'Soldadura', qty: 50, unit: 'kg' },
      { desc: 'Cabos de Amarre Polipropileno', cat: 'Cabos y Cables', qty: 200, unit: 'metros' },
      { desc: 'Grasa Marina Multiuso', cat: 'Lubricantes', qty: 30, unit: 'kg' },
    ];

    // Build realistic deliveries with varying statuses
    const deliveries: Delivery[] = [];

    const statusDistribution: { status: DeliveryStatus; hoursOffset: number }[] = [
      // Delayed entries - ETA was in the past
      { status: 'delayed', hoursOffset: -12 },
      { status: 'delayed', hoursOffset: -6 },
      { status: 'delayed', hoursOffset: -3 },
      // At port - arrived now or recently
      { status: 'at_port', hoursOffset: 0 },
      { status: 'at_port', hoursOffset: -1 },
      { status: 'at_port', hoursOffset: 0.5 },
      // In transit - arriving soon
      { status: 'in_transit', hoursOffset: 2 },
      { status: 'in_transit', hoursOffset: 4 },
      { status: 'in_transit', hoursOffset: 6 },
      // Scheduled - future
      { status: 'scheduled', hoursOffset: 12 },
      { status: 'scheduled', hoursOffset: 24 },
      { status: 'scheduled', hoursOffset: 36 },
      { status: 'scheduled', hoursOffset: 48 },
      // Received - already done
      { status: 'received', hoursOffset: -24 },
      { status: 'received', hoursOffset: -18 },
      // Receiving - in process now
      { status: 'receiving', hoursOffset: -0.5 },
      // Cancelled
      { status: 'cancelled', hoursOffset: 8 },
      // More variety
      { status: 'in_transit', hoursOffset: 1 },
      { status: 'at_port', hoursOffset: -0.25 },
      { status: 'scheduled', hoursOffset: 72 },
    ];

    statusDistribution.forEach((entry, index) => {
      const supplier = topSuppliers[index % topSuppliers.length];
      const location = selectedLocations[index % selectedLocations.length];
      const template = itemTemplates[index % itemTemplates.length];

      // Some deliveries have multiple items
      const items: DeliveryItem[] = [
        {
          id: `item-${index}-0`,
          description: template.desc,
          category: categories.length > 0
            ? categories[Math.floor(Math.random() * Math.min(50, categories.length))]
            : template.cat,
          quantity: template.qty,
          unit: template.unit,
          receivedQuantity: entry.status === 'received' ? template.qty : null,
        },
      ];

      // 40% chance of a second item
      if (index % 5 < 2) {
        const template2 = itemTemplates[(index + 7) % itemTemplates.length];
        items.push({
          id: `item-${index}-1`,
          description: template2.desc,
          category: categories.length > 0
            ? categories[Math.floor(Math.random() * Math.min(50, categories.length))]
            : template2.cat,
          quantity: template2.qty,
          unit: template2.unit,
          receivedQuantity: entry.status === 'received' ? template2.qty : null,
        });
      }

      const eta = new Date(now.getTime() + entry.hoursOffset * 60 * 60 * 1000);

      const delivery: Delivery = {
        id: `delivery-${String(index + 1).padStart(3, '0')}`,
        supplierId: supplier
          ? `supplier-${supplier.supplier.toLowerCase().replace(/\s+/g, '-')}`
          : `supplier-${index}`,
        supplierName: supplier ? supplier.supplier : `Proveedor ${index + 1}`,
        locationId: `loc-${location.toLowerCase().replace(/\s+/g, '-')}`,
        locationName: location,
        items,
        status: entry.status,
        estimatedArrival: eta.toISOString(),
        actualArrival: ['at_port', 'receiving', 'received'].includes(entry.status)
          ? new Date(eta.getTime() + (Math.random() * 2 - 1) * 30 * 60 * 1000).toISOString()
          : undefined,
        notes: index % 4 === 0
          ? 'Entrega urgente - priorizar despacho'
          : index % 6 === 0
            ? 'Requiere documentación aduanera'
            : undefined,
        purchaseOrderRef: `PO-PY-${1000 + index}`,
        createdAt: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: now.toISOString(),
      };

      deliveries.push(delivery);
    });

    console.log(`✅ [DeliveryService] Generated ${deliveries.length} mock deliveries`);
    return deliveries;
  }
}

// Singleton instance
export const deliveryService = new DeliveryService();
